import {
  CircularProgress,
  inputClasses,
  MenuItem,
  Select,
  selectClasses,
  Toolbar,
} from '@mui/material';
import React, { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { styled } from 'styled-components';

import chevronLeftIcon from '../../assets/icons/icon_chevron-left.svg';
import chevronRightIcon from '../../assets/icons/icon_chevron-right.svg';

import { MobileCheckboxWithText } from './CheckboxWithText';
import {
  RenderTableProps,
  TableInformation,
  RendererProps,
} from './SortableTable/PresentationalTable';
import { ElementWrapper } from './SortableTable/RenderTable';
import { HscanTablePagination } from './SortableTable/Table';
import { HscanTableCheckedCountText } from './SortableTable/Table';
import { Spacer } from './Spacer';

export const RowContainer = styled.tr`
  width: 100%;
  font-size: 14px;
`;
export const RowLabel = styled.th`
  width: 132px;
  text-align: center;
  color: #666973;
  background-color: #f3f4f6;
  padding: 19px 0px;
  white-space: nowrap;
  border-top: 1px solid #e7e7e7;

  tbody & {
    font-weight: normal;
  }
  tfoot &,
  thead & {
    color: black;
  }
`;

export type TextAlign = 'center' | 'justify' | 'left' | 'right' | undefined;

export const RowValue = styled.td`
  flex: 1;
  color: #5a6473;
  background-color: white;
  padding: 19px 17px;
  border-top: 1px solid #f5f5f5;
  word-break: break-word;

  tfoot &,
  thead & {
    color: black;
    font-weight: bold;
  }
`;

export const MobileTable = styled.table<{ $round?: boolean }>`
  ${p => (p.$round ? 'border-radius: 8px;' : '')}
  border-collapse: collapse;
  border: 1px solid #f5f5f5;
  width: 100%;
`;

interface MobileTableRowProps {
  label: React.ReactNode;
  align?: TextAlign;
  children: React.ReactNode;
  onClick?: () => void;
}
export const MobileTableRow = (props: MobileTableRowProps) => {
  const { label, children, align, onClick } = props;
  return (
    <RowContainer onClick={onClick}>
      <RowLabel>{label}</RowLabel>
      <RowValue align={align}>{children}</RowValue>
    </RowContainer>
  );
};

export const FullRowLabel = styled.td`
  text-align: center;
  color: #666973;
  background-color: #f3f4f6;
  padding: 19px 0px;
  white-space: nowrap;
  border-top: 1px solid #e7e7e7;

  tbody & {
    font-weight: normal;
  }
  tfoot &,
  thead & {
    color: black;
  }
`;
export const SubRowLabel = styled.td`
  text-align: center;
  color: #666973;
  background-color: #f8f9fd;
  padding: 19px 0px;
  white-space: nowrap;
  border-top: 1px solid #e7e7e7;

  tbody & {
    font-weight: normal;
  }
  tfoot &,
  thead & {
    color: black;
  }
`;

interface MobileTableFullRowProps {
  label: React.ReactNode;
  onClick?: () => void;
}
export const MobileTableFullRow = (props: MobileTableFullRowProps) => {
  const { label, onClick } = props;
  return (
    <RowContainer onClick={onClick}>
      <FullRowLabel colSpan={2}>{label}</FullRowLabel>
    </RowContainer>
  );
};
export const MobileTableSubFullRow = (props: MobileTableFullRowProps) => {
  const { label, onClick } = props;
  return (
    <RowContainer onClick={onClick}>
      <SubRowLabel colSpan={2}>{label}</SubRowLabel>
    </RowContainer>
  );
};

const TableContainer = styled.div`
  width: 100%;
  min-width: fit-content;
  overflow: auto;
`;
const TableContentContainer = styled.div`
  padding-top: 24px;
  padding-right: 20px;
  padding-bottom: 24px;
  padding-left: 20px;
  border-radius: 8px;
  background-color: white;
`;
const ElementContainer = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 44px;
`;

interface MobileTableWrapperProps<D, K extends keyof D>
  extends RendererProps<D, K>,
    RenderTableProps<D, K> {}
export const MobileTableWrapper = <D, K extends keyof D>(
  props: MobileTableWrapperProps<D, K>,
) => {
  const {
    Header = () => null,
    Element = () => null,
    Footer = () => null,
    renderLoading = MobileLoadingIndicator,
    renderEmpty,
    tableContext: TableContext,
    elementContext: TableElementContext,
    ElementProvider: TableElementInformationProvider,
  } = props;
  const tableInfo = useContext(TableContext);
  const { dataToShow, isLoading, identifyKey, totalCount } = tableInfo;

  // 한 번도 로딩된 적없고 비어있을 경우
  // -> 헤더와 푸터가 없이 덩그러니 render empty
  // 첫 로딩인 경우
  // -> 헤더와 푸터가 없이 덩그러니 render loading
  // 로딩 했는데도 없는 경우
  // -> 헤더와 푸터는 없고 render empty
  // 데이터가 있고, 페이지네이션 등을 눌러서 로딩한 경우
  // -> 헤더와 푸터는 있고 render loading

  return (
    <TableContainer>
      {totalCount === 0 ? (
        <TableContentContainer>
          {isLoading ? renderLoading?.(tableInfo) : renderEmpty?.(tableInfo)}
        </TableContentContainer>
      ) : (
        <>
          <TableContentContainer>
            <Header {...tableInfo} />
            <ElementContainer>
              {isLoading ? (
                renderLoading?.(tableInfo)
              ) : (
                <>
                  {!dataToShow.length && renderEmpty?.(tableInfo)}
                  {dataToShow.map((element, elementIndex) => (
                    <TableElementInformationProvider
                      key={`${element[identifyKey]}`}
                      element={element}
                      elementIndex={elementIndex}>
                      <ElementWrapper
                        tableContext={TableContext}
                        elementContext={TableElementContext}
                        Element={Element}
                      />
                    </TableElementInformationProvider>
                  ))}
                </>
              )}
            </ElementContainer>
          </TableContentContainer>
          <Spacer height="33px" />
          <Footer {...tableInfo} />
        </>
      )}
    </TableContainer>
  );
};

const FooterContentContainer = styled.div`
  display: flex;
  justify-content: center;
`;
const FooterContainer = styled.div`
  display: flex;
  border: 1px solid #e9e9e9;
  border-radius: 6px;
  overflow: hidden;
`;
const NavigateButton = styled.button<{ $icon: string }>`
  background: none;
  border-style: none;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 50px;
  height: 50px;
  background-color: ${p => (p.disabled ? '#f3f3f3' : 'white')};
  background-image: url(${p => p.$icon});
  background-repeat: no-repeat;
  background-position: 50%;
`;
const PageButton = styled.button<{ $selected?: boolean; $last?: boolean }>`
  background: none;
  border-style: none;
  text-align: center;
  ${p =>
    p.$selected
      ? 'background-color: #2e5be0; color: white;'
      : 'background-color: white; color: #2e5be0;'}
  font-weight: bold;
  font-size: 16px;
  border-left: 1px solid #e9e9e9;
  padding: 0px 20px;
  ${p => (p.$last ? 'border-right: 1px solid #e9e9e9' : '')}
`;

interface MobileImageTableFooterProps<D, K extends keyof D>
  extends TableInformation<D, K> {
  paginationItemCount?: number;
}
export const MobileTableFooter = <D, K extends keyof D>(
  props: MobileImageTableFooterProps<D, K>,
) => {
  const {
    totalCount,
    countPerPage,
    page,
    onChangePage,
    paginationItemCount = 5,
  } = props;
  /**
   * pagination item 중 제일 왼쪽에 있는 기준이 되는 item (0-index)
   * * 0 1 2 3 4 -> 0
   * * 5 6 7 8 9 -> 5
   */
  const currentPaginationBase =
    Math.floor(page / paginationItemCount) * paginationItemCount;
  /**
   * totalPages (0-index)
   */
  const totalPages = Math.ceil(totalCount / countPerPage) - 1;
  const pageCounts = useMemo(() => {
    const pages = [];
    for (
      let i = 0;
      i < Math.min(totalPages - currentPaginationBase + 1, paginationItemCount);
      i++
    ) {
      pages.push(currentPaginationBase + i);
    }
    return pages;
  }, [totalPages, currentPaginationBase, paginationItemCount]);
  const isLastPaginationItem =
    pageCounts?.[pageCounts.length - 1] === totalPages;
  const onClickPrev = () => {
    onChangePage?.(currentPaginationBase - 1);
  };
  const onClickNext = () => {
    onChangePage?.(currentPaginationBase + paginationItemCount);
  };
  return (
    <FooterContentContainer>
      <FooterContainer>
        <NavigateButton
          onClick={onClickPrev}
          disabled={pageCounts?.[0] === 0}
          $icon={chevronLeftIcon}
        />
        {pageCounts.map(pageNum => (
          <PageButton
            onClick={() => onChangePage?.(pageNum)}
            key={pageNum}
            $selected={page === pageNum}
            $last={pageNum === pageCounts.length - 1}>
            {pageNum + 1}
          </PageButton>
        ))}
        <NavigateButton
          onClick={onClickNext}
          disabled={isLastPaginationItem}
          $icon={chevronRightIcon}
        />
      </FooterContainer>
    </FooterContentContainer>
  );
};

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  margin-bottom: 31px;
`;

interface MobileSelectAllHeaderProps<D, K extends keyof D>
  extends TableInformation<D, K> {
  selectAllText: string;
  selectedCountText?: string;
}
export const MobileSelectAllHeader = <D, K extends keyof D>(
  props: MobileSelectAllHeaderProps<D, K>,
) => {
  const {
    onSelectAll,
    selectables,
    currentSelected,
    selectAllText,
    selectedCountText,
  } = props;

  return (
    <HeaderContainer>
      <MobileCheckboxWithText
        value={
          selectables.length > 0 &&
          selectables.length === currentSelected.length
        }
        onChange={e => onSelectAll?.(e, selectables)}
        text={selectAllText}
      />
      <HscanTableCheckedCountText>
        {selectedCountText}
      </HscanTableCheckedCountText>
    </HeaderContainer>
  );
};

const PaginationSpacer = styled.div`
  flex: 1 1 100%;
`;
const SelectLabel = styled.p`
  flex-shrink: 0;
  font-size: 14px;
  color: #000000de;
`;
export const MobileRowsPerPage = <D, K extends keyof D>({
  countPerPage,
  countPerPageOptions,
  onChangeCountPerPage,
}: TableInformation<D, K>) => {
  const { t } = useTranslation();

  return (
    <div className="MuiTablePagination-root">
      <Toolbar>
        <PaginationSpacer />
        <SelectLabel className="MuiTablePagination-selectLabel">
          {t('common.table.countPerPage')}
        </SelectLabel>
        <Select
          value={countPerPage}
          onChange={event => onChangeCountPerPage?.(+event.target.value)}
          variant="standard"
          sx={{
            '&::before, &::after': {
              content: 'none',
            },
            [`&.${inputClasses.root}`]: {
              flexShrink: 0,
              marginLeft: '8px',
            },
            [`.${selectClasses.select}`]: {
              fontSize: '14px',
              padding: '8px',
            },
          }}>
          {countPerPageOptions.map(option => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </Select>
      </Toolbar>
    </div>
  );
};

export const MobilePagination = <D, K extends keyof D>(
  props: TableInformation<D, K>,
) => {
  const {
    totalCount,
    countPerPage,
    countPerPageOptions,
    page,
    onChangePage,
    onChangeCountPerPage,
  } = props;

  const { t } = useTranslation();

  return (
    <HscanTablePagination
      rowsPerPageOptions={countPerPageOptions}
      labelRowsPerPage={t('common.table.countPerPage')}
      component="div"
      count={totalCount}
      rowsPerPage={countPerPage}
      page={page}
      onPageChange={(_, newPage) => onChangePage?.(newPage)}
      onRowsPerPageChange={(event: React.ChangeEvent<HTMLInputElement>) => {
        onChangeCountPerPage?.(parseInt(event.target.value, 10));
        onChangePage?.(0);
      }}
    />
  );
};

export const MobilePaginationWithoutRowsPerPage = <D, K extends keyof D>(
  props: TableInformation<D, K>,
) => <MobilePagination {...props} countPerPageOptions={[]} />;

const MobileLoadingIndicatorWrapper = styled.div`
  width: 100%;
  display: flex;
  margin: 0 auto;
  justify-content: center;
`;
export const MobileLoadingIndicator = () => (
  <MobileLoadingIndicatorWrapper>
    <CircularProgress />
  </MobileLoadingIndicatorWrapper>
);
